/**
 * Warm the common in-app route chunks once the browser is idle, so navigating
 * to a page never waits on a network round-trip for its JavaScript.
 *
 * Pure preload: each import() below uses the EXACT same module specifier as the
 * route's lazy() importer in v2-routes.tsx, so Vite serves ONE shared chunk
 * (no double download) and React.lazy() resolves instantly on navigation. This
 * only loads module code — no side effects, no behavior change.
 *
 * Imports are staggered one-at-a-time on requestIdleCallback so prefetching
 * never competes with the current page's own work or the user's interactions.
 */

let started = false;

export function prefetchRoutes(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  // Ordered by how likely the user visits them next from the dashboard.
  const importers: Array<() => Promise<unknown>> = [
    () => import('@/pages/v2/Home'),
    () => import('@/pages/v2/CVBuilder'),
    () => import('@/pages/v2/analysis/ProfileInput'),
    () => import('@/pages/v2/Posts'),
    () => import('@/pages/v2/Profile'),
    () => import('@/pages/v2/CVPreflight'),
    () => import('@/pages/v2/ContentPreflight'),
    () => import('@/pages/v2/CoverLetter'),
    () => import('@/pages/v2/Billing'),
    () => import('@/pages/v2/Pricing'),
    () => import('@/pages/v2/Notifications'),
  ];

  const ric = (window as Window & {
    requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void;
  }).requestIdleCallback;
  const idle = (cb: () => void) =>
    typeof ric === 'function' ? ric(cb, { timeout: 2000 }) : window.setTimeout(cb, 300);

  let i = 0;
  const next = () => {
    if (i >= importers.length) return;
    const run = importers[i++];
    run().catch(() => { /* prefetch is best-effort */ }).finally(() => idle(next));
  };
  idle(next);
}
